import { Location } from "@angular/common";
import { Injectable, NgZone } from "@angular/core";
import {
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
} from "@angular/router";

// Entry bundles as emitted by `npm run build` (outputHashing: all), e.g. main-AB12CD34.js.
const HASHED_ENTRY = /^(main|polyfills|scripts|styles)-[A-Z0-9]{8}\.(js|css)$/;
const CHUNK_LOAD_ERROR = /dynamically imported module|Importing a module script failed/i;

const CHECK_INTERVAL_MS = 5 * 60 * 1000;
const MIN_CHECK_GAP_MS = 60 * 1000;
const CHUNK_RETRY_GAP_MS = 30 * 1000;

const RELOAD_TARGET_KEY = "app-update:reload-target";
const CHUNK_RELOAD_KEY = "app-update:chunk-reload-at";

/**
 * Moves open tabs onto a newly deployed build without anyone hard-refreshing.
 *
 * Every deploy publishes new content-hashed bundle names plus an index.html
 * served with `Cache-Control: no-cache` (see tools/deploy-s3.cjs). This service
 * re-reads index.html in the background; once its bundle names differ from the
 * ones this tab booted with, the next route change is done as a full page load
 * of that same URL, so the user simply lands on the new build.
 *
 * It also recovers when a lazy chunk of the running build is no longer on the
 * server, which otherwise leaves navigation dead until a manual refresh.
 */
@Injectable({ providedIn: "root" })
export class AppUpdateService {
  private readonly currentBuild = buildSignature(document);
  private latestBuild: string | null = null;
  private lastCheckAt = 0;
  private started = false;

  constructor(
    private router: Router,
    private location: Location,
    private zone: NgZone
  ) {}

  start(): void {
    if (this.started) return;
    this.started = true;

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.loadNewBuildOnRouteChange(event);
      } else if (event instanceof NavigationEnd) {
        this.checkForUpdate();
      } else if (
        event instanceof NavigationError &&
        CHUNK_LOAD_ERROR.test(String(event.error?.message ?? event.error))
      ) {
        this.recoverFromMissingChunk(event.url);
      }
    });

    // `ng serve` emits unhashed bundles, so there is no build identity to compare.
    if (!this.currentBuild) return;

    if (readSession(RELOAD_TARGET_KEY) === this.currentBuild) {
      removeSession(RELOAD_TARGET_KEY);
    }

    this.zone.runOutsideAngular(() => {
      setInterval(() => {
        if (!document.hidden) this.checkForUpdate();
      }, CHECK_INTERVAL_MS);
      document.addEventListener("visibilitychange", () => {
        if (!document.hidden) this.checkForUpdate();
      });
    });
  }

  private get updatePending(): boolean {
    return (
      !!this.latestBuild &&
      this.latestBuild !== this.currentBuild &&
      // A previous reload towards this build still came back with the old one
      // (e.g. a browser cache from before deploys set Cache-Control). Do not
      // keep reloading on every navigation for the rest of the session.
      this.latestBuild !== readSession(RELOAD_TARGET_KEY)
    );
  }

  private loadNewBuildOnRouteChange(event: NavigationStart): void {
    if (!this.updatePending) return;

    const extras = this.router.getCurrentNavigation()?.extras;
    // Query-param or fragment-only changes (filters, tabs) keep their in-page state.
    if (extras?.skipLocationChange || pathOf(event.url) === pathOf(this.router.url)) {
      return;
    }

    writeSession(RELOAD_TARGET_KEY, this.latestBuild!);
    if (event.navigationTrigger === "popstate") {
      // Back/forward has already put the target URL in the address bar.
      window.location.reload();
    } else if (extras?.replaceUrl) {
      window.location.replace(this.location.prepareExternalUrl(event.url));
    } else {
      window.location.assign(this.location.prepareExternalUrl(event.url));
    }
  }

  private recoverFromMissingChunk(url: string): void {
    const lastAttempt = Number(readSession(CHUNK_RELOAD_KEY) || 0);
    if (Date.now() - lastAttempt < CHUNK_RETRY_GAP_MS) return;

    writeSession(CHUNK_RELOAD_KEY, String(Date.now()));
    window.location.assign(this.location.prepareExternalUrl(url));
  }

  private checkForUpdate(): void {
    if (!this.currentBuild || Date.now() - this.lastCheckAt < MIN_CHECK_GAP_MS) {
      return;
    }
    this.lastCheckAt = Date.now();

    // Plain fetch rather than HttpClient: this must not go through the API
    // interceptors (auth headers, error toasts, logout-on-failure).
    fetch(new URL("index.html", document.baseURI).href, { cache: "no-store" })
      .then((response) => (response.ok ? response.text() : null))
      .then((html) => {
        if (!html) return;
        const latest = buildSignature(
          new DOMParser().parseFromString(html, "text/html")
        );
        if (latest) this.latestBuild = latest;
      })
      .catch(() => {
        // Offline or blocked; the next check will try again.
      });
  }
}

/** The hashed entry bundles a document loads, e.g. "main-AB12CD34.js,styles-EF56GH78.css". */
function buildSignature(doc: Document): string | null {
  const names = Array.from(
    doc.querySelectorAll('script[src], link[rel="stylesheet"][href]')
  )
    .map((el) =>
      (el.getAttribute("src") ?? el.getAttribute("href") ?? "")
        .split(/[?#]/)[0]
        .split("/")
        .pop()!
    )
    .filter((name) => HASHED_ENTRY.test(name))
    .sort();
  return names.length ? names.join(",") : null;
}

function pathOf(url: string): string {
  return url.split(/[?#]/)[0];
}

function readSession(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeSession(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // Storage unavailable (private mode quota etc.); the reload still happens.
  }
}

function removeSession(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {}
}
